<template>
  <div v-if="data && data.tenantCount > 0" class="fcd">
    <button class="fcd__strip" @click="open = true">
      <span class="mdi mdi-archive-alert-outline fcd__icon"></span>
      <span class="fcd__text">
        <strong>Adeudos de contratos terminados:</strong>
        {{ formatCurrency(data.totalMxn) }}<template v-if="data.totalUsd > 0"> + {{ formatUsd(data.totalUsd) }}</template>
        · {{ data.tenantCount }} inquilino{{ data.tenantCount === 1 ? '' : 's' }}
        <span class="fcd__hint">No se incluyen en las tarjetas ni en la lista</span>
      </span>
      <span class="fcd__link">Ver detalle</span>
    </button>

    <Teleport to="body">
      <div v-if="open" class="fcd-overlay" @click.self="open = false">
        <div class="fcd-modal" role="dialog" aria-modal="true">
          <div class="fcd-modal__header">
            <span class="fcd-modal__title">Adeudos de contratos terminados</span>
            <button class="fcd-modal__close" @click="open = false"><span class="mdi mdi-close"></span></button>
          </div>

          <div class="fcd-modal__body">
            <p class="fcd-modal__text">
              Son cargos vencidos de contratos que ya terminaron o cuya fecha de fin pasó. El dinero se sigue debiendo, pero estos
              inquilinos ya no aparecen en la lista de Cobranza. Si te pagan, usa <strong>Registrar pago</strong> (completo o un abono).
              Si decides no cobrarlo, puedes <strong>condonar</strong> el saldo de cada cargo: lo ya pagado se conserva y el saldo
              desaparece. Condonar no se puede deshacer.
            </p>
            <p v-if="error" class="fcd-modal__error">{{ error }}</p>

            <div v-for="c in data.contracts" :key="c.contractId" class="fcd-tenant">
              <div class="fcd-tenant__head">
                <div>
                  <span class="fcd-tenant__name">{{ c.tenantName }}</span>
                  <span class="fcd-tenant__sub">{{ c.plotName }} · {{ statusText(c) }}</span>
                </div>
                <div class="fcd-tenant__total">
                  {{ formatCurrency(c.totalMxn) }}<template v-if="c.totalUsd > 0"> + {{ formatUsd(c.totalUsd) }}</template>
                </div>
                <button class="fcd-btn fcd-btn--danger" :disabled="busy" @click="askForgive('contract:' + c.contractId)">
                  {{ confirmKey === 'contract:' + c.contractId ? 'Confirmar: condonar todo' : 'Condonar todo' }}
                </button>
              </div>

              <table class="fcd-table">
                <thead><tr><th>Cargo</th><th>Vencía</th><th class="r">Saldo</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="ch in c.charges" :key="ch.id">
                    <td>{{ ch.concept }}<span class="fcd-table__sub">{{ periodText(ch) }}</span></td>
                    <td>{{ formatDate(ch.dueDate) }}</td>
                    <td class="r">{{ ch.currency === 'MXN' ? formatCurrency(ch.remaining) : formatUsd(ch.remaining) }}</td>
                    <td class="r fcd-table__actions">
                      <button class="fcd-btn fcd-btn--primary" :disabled="busy" @click="startPay(ch)">Registrar pago</button>
                      <button class="fcd-btn" :disabled="busy" @click="askForgive('charge:' + ch.id)">
                        {{ confirmKey === 'charge:' + ch.id ? 'Confirmar' : 'Condonar' }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="payingId && c.charges.some((x) => x.id === payingId)" class="fcd-pay-row">
                    <td colspan="4">
                      <form class="fcd-pay" @submit.prevent="submitPay(c)">
                        <span class="fcd-pay__label">Pago a: {{ payingCharge?.concept }}</span>
                        <span class="fcd-pay__money">{{ payingCharge?.currency === 'MXN' ? '$' : 'US$' }}
                          <input v-model.number="pay.amount" type="number" min="0.01" step="0.01" :max="payingCharge?.remaining" class="fcd-pay__input" />
                        </span>
                        <input v-model="pay.date" type="date" class="fcd-pay__input" />
                        <input v-model="pay.reference" class="fcd-pay__input fcd-pay__input--wide" placeholder="Referencia (opcional)" maxlength="80" />
                        <button type="submit" class="fcd-btn fcd-btn--primary" :disabled="busy || !validPay">{{ busy ? 'Guardando...' : 'Guardar pago' }}</button>
                        <button type="button" class="fcd-btn" :disabled="busy" @click="payingId = ''">Cancelar</button>
                      </form>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getFormerDebts, forgiveCharge, registerPayment } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, required: true },
  reloadKey: { type: Number, default: 0 },
})
const emit = defineEmits(['changed'])

const data = ref(null)
const open = ref(false)
const busy = ref(false)
const error = ref('')
const confirmKey = ref('')

async function load() {
  try {
    data.value = await getFormerDebts(props.parkId)
    if (!data.value.tenantCount) open.value = false
  } catch {
    data.value = null
  }
}
onMounted(load)
watch(() => props.reloadKey, load)

// --- Registrar pago (completo o abono) a un cargo de un contrato terminado ---
const payingId = ref('')
const pay = ref({ amount: null, date: '', reference: '' })
const payingCharge = computed(() => {
  for (const c of data.value?.contracts || []) {
    const ch = c.charges.find((x) => x.id === payingId.value)
    if (ch) return ch
  }
  return null
})
const validPay = computed(() => payingCharge.value && pay.value.amount > 0 && pay.value.amount <= payingCharge.value.remaining + 0.005 && !!pay.value.date)

function startPay(ch) {
  error.value = ''
  confirmKey.value = ''
  payingId.value = ch.id
  pay.value = { amount: Math.round(ch.remaining * 100) / 100, date: new Date().toISOString().slice(0, 10), reference: '' }
}

async function submitPay(contract) {
  const ch = payingCharge.value
  if (!ch || !validPay.value) return
  busy.value = true
  error.value = ''
  try {
    await registerPayment(contract.contractId, {
      amount: pay.value.amount,
      date: pay.value.date,
      concept: 'SPECIFIC_CHARGE',
      chargeId: ch.id,
      reference: pay.value.reference || undefined,
      periodYear: ch.periodYear,
      periodMonth: ch.periodMonth,
    })
    payingId.value = ''
    await load()
    emit('changed')
  } catch (e) {
    error.value = e.message
  } finally {
    busy.value = false
  }
}

// Primer clic pide confirmación; el segundo ejecuta.
async function askForgive(key) {
  error.value = ''
  payingId.value = ''
  if (confirmKey.value !== key) { confirmKey.value = key; return }
  confirmKey.value = ''
  busy.value = true
  try {
    const [kind, id] = key.split(':')
    const ids = kind === 'charge' ? [id] : data.value.contracts.find((c) => c.contractId === id).charges.map((c) => c.id)
    for (const chargeId of ids) await forgiveCharge(chargeId)
    await load()
    emit('changed')
  } catch (e) {
    error.value = e.message
    await load()
  } finally {
    busy.value = false
  }
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const periodText = (ch) => `Periodo ${MONTHS[ch.periodMonth - 1]} ${ch.periodYear}`
const statusText = (c) => {
  const end = formatDate(c.endDate)
  return c.contractStatus === 'ENDED' ? `Contrato terminado (fin ${end})` : `Contrato vencido el ${end}`
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0)
}
function formatUsd(n) { return `US$ ${Math.round(n || 0).toLocaleString('es-MX')}` }
</script>

<style scoped>
.fcd { padding: 0 1.5rem; }
.fcd__strip { width: 100%; display: flex; align-items: center; gap: 0.6rem; padding: 0.55rem 0.9rem; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; cursor: pointer; text-align: left; font-family: var(--font-body); font-size: 12px; color: #92400e; }
.fcd__strip:hover { background: #fef3c7; }
.fcd__icon { font-size: 16px; }
.fcd__text { flex: 1; min-width: 0; }
.fcd__hint { display: block; font-size: 11px; color: #b45309; opacity: 0.8; }
.fcd__link { font-weight: 500; text-decoration: underline; white-space: nowrap; }

.fcd-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 1rem; }
.fcd-modal { width: 720px; max-width: 100%; max-height: 85vh; background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column; overflow: hidden; text-align: left; }
.fcd-modal__header { flex-shrink: 0; background: #0f172a; display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.25rem; }
.fcd-modal__title { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #fff; }
.fcd-modal__close { background: none; border: none; color: rgba(255, 255, 255, 0.6); cursor: pointer; display: flex; }
.fcd-modal__body { padding: 1.1rem 1.25rem 1.25rem; overflow-y: auto; font-family: var(--font-body); color: #334155; }
.fcd-modal__text { margin: 0 0 0.9rem; font-size: 13px; line-height: 1.5; }
.fcd-modal__error { margin: 0 0 0.7rem; font-size: 12px; color: #dc2626; }

.fcd-tenant { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.7rem 0.8rem; margin-bottom: 0.8rem; }
.fcd-tenant__head { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 0.5rem; }
.fcd-tenant__head > div:first-child { flex: 1; min-width: 0; }
.fcd-tenant__name { display: block; font-size: 13px; font-weight: 600; color: #1e3a5f; }
.fcd-tenant__sub { display: block; font-size: 11px; color: #94a3b8; }
.fcd-tenant__total { font-family: var(--font-mono); font-weight: 700; font-size: 13px; color: #dc2626; white-space: nowrap; }

.fcd-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.fcd-table th { text-align: left; font-size: 10px; text-transform: uppercase; color: #94a3b8; padding-bottom: 0.3rem; border-bottom: 1px solid #e2e8f0; }
.fcd-table td { padding: 0.4rem 0.3rem 0.4rem 0; border-bottom: 1px solid #f1f5f9; color: #1e3a5f; vertical-align: top; }
.fcd-table .r { text-align: right; font-family: var(--font-mono); white-space: nowrap; }
.fcd-table__sub { display: block; font-size: 10px; color: #94a3b8; font-family: var(--font-body); }

.fcd-btn { background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.25rem 0.6rem; font-family: var(--font-body); font-size: 11px; color: #475569; cursor: pointer; }
.fcd-btn:hover:not(:disabled) { border-color: #3b82f6; color: #3b82f6; }
.fcd-btn:disabled { opacity: 0.5; cursor: default; }
.fcd-table__actions { display: flex; gap: 0.35rem; justify-content: flex-end; flex-wrap: wrap; }
.fcd-pay-row td { background: #f8fafc; }
.fcd-pay { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.3rem 0; font-family: var(--font-body); }
.fcd-pay__label { font-size: 11px; color: #64748b; flex-basis: 100%; }
.fcd-pay__money { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 12px; color: #64748b; }
.fcd-pay__input { border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.25rem 0.4rem; font-size: 12px; width: 110px; font-family: var(--font-mono); }
.fcd-pay__input--wide { width: 170px; font-family: var(--font-body); }
.fcd-btn--primary { border-color: #3b82f6; color: #3b82f6; }
.fcd-btn--primary:hover:not(:disabled) { background: #eff6ff; }
.fcd-btn--danger { border-color: #fca5a5; color: #dc2626; }
.fcd-btn--danger:hover:not(:disabled) { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
</style>